/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { LinearProgress, Button} from '@mui/material';
import LegacyGrid from './legacy/LegacyGrid';
import Avatar from '@mui/material/Avatar';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TableContainer from '@mui/material/TableContainer';
import { Theme } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import autoTable from 'jspdf-autotable';
import * as React from 'react';
import { connect } from 'react-redux';
// removed react-table-container (unused)
import { bindActionCreators } from 'redux';
import { categories, Category, CategoryType } from '../common/Categories';
import { getDateDiff, sendRequestAndReceiveResponse } from '../common/common';
import { GridGraphqlExtras, toServiceCaseGridGraphqlBody } from '../common/graphqlJsonBody';
import { fetchLicensesEndPoint, fetchServiceCasesEndPoint } from '../common/constants';
import { exportLicenseColumnAttributesPdf, LicenseData } from '../common/LicenseModel';
import {
    loadFromLocalStorage
} from '../common/localStorage';
import { exportAllServiceCaseColumnAttributesPdf, exportServiceCaseColumnAttributesPdf, ServiceCaseData } from '../common/ServiceCaseModel';
import { Module, RoleModuleMapping, User, UserRole, UserRoleMapping } from '../common/UserModel';
import { actionCreators } from '../store/DataTable';
import SimpleDialogDemo from './AssignToDialog';
import './DataTable.css';
import Edit from './Edit';
import Export from './Export';
import Open from './Open';
import SearchSection from './SearchSection';
import './styles.css';
import TableViewCol from './TableViewCol';
import LicenseStatusPill from './LicenseStatusPill';


import classNames from 'classnames';
import jsPDF from 'jspdf';

const styles = (theme: Theme) => ({
    currency: {
        fontWeight: ((theme && (theme as any).typography && (theme as any).typography.fontWeightMedium) ? (theme as any).typography.fontWeightMedium : 500)
    },
    numericInput: {
        width: '100%'
    },
    avatar: {
        width: '12px',
        height: '12px',
        display: 'inline-block',
        marginLeft: '10px',
        marginBottom: '-2px',
        marginRight: '2px'
    },
    blackAvatar: {
        backgroundColor: '#000000'
    },
    redAvatar: {
        backgroundColor: '#a51d25'
    },
    orangeAvatar: {
        backgroundColor: '#ff8b00'
    },
    greyAvatar: {
        backgroundColor: '#bfbfc6'
    },
    greenAvatar: {
        backgroundColor: '#4CAF50'
    },
    blueAvatar: {
        backgroundColor: '#045891'
    },
    legendRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        flexWrap: 'wrap'
    },
    legendItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
    },
    legendPill: {
        width: '22px',
        height: '12px',
        display: 'inline-block',
        borderRadius: '999px',
        margin: '0',
        position: 'relative',
        top: '2px',
        marginRight: '6px',
        marginLeft: '10px'
    },
    legendNumberPill: {
        minWidth: '26px',
        height: '22px',
        borderRadius: '999px',
        backgroundColor: '#9E9E9E',
        color: '#fff',
        fontSize: '12px',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    agingText: {
        fontFamily: 'Roboto',
        fontSize: '12px'
    },
    showColumnButton: {
        margin: '20px 0'
    },
    root: {
        display: 'flex',
        flex: 1,
        justifyContent: 'flex-end'
    }
});

const getInputValue = (value?: string): string =>
    value === undefined ? '':value;

const getColor = (value: number, selectedCategoryType: CategoryType, licenseStatus?: string): string | undefined => {
    const normalizedLicenseStatus = (licenseStatus || '').toLowerCase();
    switch (selectedCategoryType) {
        case CategoryType.ServiceCase:
            if (value < 7) {
                return '#000000';
            }
            if (value < 30) {
                return '#ff8b00';
            }

            return '#a51d25';
        case CategoryType.License:
            // For Unused or AutoProvisioned, always use black text (no orange override)
            if (normalizedLicenseStatus === 'unused' || normalizedLicenseStatus === 'autoprovisioned') {
                return '#000000';
            }
            if (value < 0) {
                return '#bfbfc6';
            }
            if (value < 7) {
                return '#ff8b00';
            }
            return '#000000';
    }
};

interface Props {
    startDate?: Date;
    endDate?: Date;
    selectedCategory: Category | undefined;
    totalCount: number;
    gridData: any;
    requestGridData: Function;
    isLoading: boolean;
    classes?: any;
    error: any;
    refreshChart?: Function;
}
const TableSort: React.FunctionComponent<{
    active: boolean;
    direction: 'asc' | 'desc';
    sortByColumn: string;
    onClick: any;
    children?: React.ReactNode;
}> = props => {
    const clickHanlder = () => {
        props.onClick(props.sortByColumn);
    };
    return (
        <Tooltip title="Sort">
            <TableSortLabel
                active={props.active}
                direction={props.direction}
                onClick={clickHanlder}
            >
                {props.children}
            </TableSortLabel>
        </Tooltip>
    );
};
const DEFAULT_MIN_WIDTH_CELL = 70;
const DEFAULT_MAX_WIDTH_CELL = 1500;

class DataTable extends React.PureComponent<Props, any> {
    state = {
        pageSizes: [10, 20, 30, 40, 50],
        rows: undefined,
        selection: undefined,
        sortOrder: 'asc',
        sortOrderBy: '',
        filters: [],
        loading: false,
        pageSize: 10,
        currentPage: 0,
        totalCount: 0,
        selected: [] as any,
        selectedObject: [] as any,
        isViewColumnOpen: false,
        columns: [],
        isSelectOnlyOne: false,
        isCheckBoxEnabled: false,
        columnWidths: [],
        existingColumnWidths: [],
        columnIndex:1
    };
    lastQuery: string | undefined;
    columnRefs: any;
    isResizing: any;


    constructor(props: Props | Readonly<Props>) {
        super(props);
        if (this.props.selectedCategory !== undefined) {
            this.columnRefs = this.props.selectedCategory.columns.map(() => React.createRef());
        }
        this.isResizing = React.createRef();
    }

    componentDidMount() {
        this.loadData();
        if (this.props.selectedCategory !== undefined) {
            this.setState({
                columns: this.props.selectedCategory.columns, isSelectOnlyOne: this.props.selectedCategory.isSelectOneOnly,
                isCheckBoxEnabled: this.props.selectedCategory.isCheckBoxEnabled,
                columnWidths: Array(this.props.selectedCategory.columns.length).fill(0),
                existingColumnWidths: Array(this.props.selectedCategory.columns.length).fill(0)
            });
        }

        if (this.props.selectedCategory !== undefined && (this.props.selectedCategory.id === 1 || this.props.selectedCategory.id === 3)) {
            const clientMode: any = loadFromLocalStorage('Client Mode');
            if (clientMode != null && clientMode !== undefined && clientMode === 'Desktop') {
                this.setState({ isCheckBoxEnabled: true });
            }
            else {
                this.setState({ isCheckBoxEnabled: false });
            }
        }
        document.onmousemove = this.handleOnMouseMove;
        document.onmouseup = this.handleOnMouseUp;
    }

    componentWillUnmount() {
        document.onmousemove = null;
        document.onmouseup = null;
    }

    handleOnMouseMove = (e:any) => {
        if (this.isResizing.current >= 0) {
            const columnRef = this.columnRefs[this.isResizing.current];
            if (columnRef && columnRef.current && columnRef.current.parentElement) {
                const newWidth = e.clientX - columnRef.current.parentElement.getBoundingClientRect().left;
                this.adjustWidthColumn(this.isResizing.current, newWidth);
            }
        }
    };

    adjustWidthColumn = (index: number, width: number) => {
        if (this.props.selectedCategory) {
            const { selectedCategory } = this.props;
            const { columnRefs } = this;

            const tableElement = columnRefs[index].current.parentElement.parentElement.parentElement.parentElement;
            const columnElement = columnRefs[index].current.parentElement;

            const minWidth = selectedCategory.columns[index] && selectedCategory.columns[index].minWidth !== undefined
                ? selectedCategory.columns[index].minWidth : DEFAULT_MIN_WIDTH_CELL;
            const maxWidth = (columnRefs[index] && columnRefs[index].maxWidth !== undefined) ? columnRefs[index].maxWidth : DEFAULT_MAX_WIDTH_CELL;
            const newWidth = Math.min(Math.max(width, minWidth), maxWidth);

            const updatedColumnWidths: number[] = [...this.state.columnWidths];
            updatedColumnWidths[index] = newWidth;
            this.setState({ columnWidths: updatedColumnWidths });

            if (this.state.existingColumnWidths[index] === 0) {
                const updatedexistingColumnWidths: number[] = [...this.state.existingColumnWidths];
                updatedexistingColumnWidths[index] = columnElement.getBoundingClientRect().width;
                this.setState({ existingColumnWidths: updatedexistingColumnWidths });
            }
            const totalWidth = updatedColumnWidths.reduce((acc, width) => acc + width, 0);
            const existingtotalWidth = this.state.existingColumnWidths.reduce((acc, width) => acc + width, 0);
            tableElement.style.width = `calc( 100% + ${totalWidth - existingtotalWidth}px)`;
            columnElement.style.width = `${newWidth}px`;
        }
    };

    handleOnMouseUp = () => {
        this.isResizing.current = -1;
        this.setCursorDocument(false);
    };

    setCursorDocument = (isResizing:any) => {
        document.body.style.cursor = isResizing ? "col-resize" : "auto";
    };

    onClickResizeColumn = (index: any) => {
        this.isResizing.current = index;
        this.setCursorDocument(true);
    };

    resetColumnWidths = () => {
        if (this.props.selectedCategory !== undefined && this.props.selectedCategory.columns) {
            const newColumnWidths: number[] = this.state.columnWidths.map(() => 0);
            this.setState({ columnWidths: newColumnWidths });
            this.setState({ existingColumnWidths: newColumnWidths });
            this.props.selectedCategory.columns.forEach((_: any, index: any) => {
                const columnRef = this.columnRefs[index];
                if (columnRef && columnRef.current && columnRef.current.parentElement) {
                    columnRef.current.parentElement.style.width = 'auto';
                }
            });
            this.columnRefs[this.state.columnIndex].current.parentElement.parentElement.parentElement.parentElement.style.width = `100%`;
        }  
    };

    getTableRow = (rowItem: any, isSelected: boolean, selectedCategoryType: CategoryType) => {
        const fontColor = getColor(selectedCategoryType === CategoryType.ServiceCase ?
            parseInt(rowItem.age) : selectedCategoryType === CategoryType.License ? parseInt(rowItem.expiresin) : parseInt(rowItem.age), selectedCategoryType, selectedCategoryType === CategoryType.License ? rowItem.licensestatus : undefined);
        return (
            <TableRow
                className={`dtable-row ${isSelected ? 'selected' : ''}`}
                key={rowItem.id}
                hover
                onClick={event => this.handleClick(event, rowItem.id, rowItem)}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={-1}
                selected={isSelected}
            >
                {(this.props.selectedCategory !== undefined &&
                    this.state.isCheckBoxEnabled === true) &&
                    (
                        <TableCell className="dtable-cell" padding="none">
                            <Checkbox checked={isSelected} />
                        </TableCell>
                    )}
                {this.props.selectedCategory !== undefined && this.state.columns.map((column: any, index: any) => {
                    return (

                        (column.display === true) && <TableCell
                            className="dtable-cell"
                            key={column.name}
                            id={column.name}
                            style={{ color: fontColor, maxWidth: column.width }}
                            title={this.getCellTitle(column.name, rowItem, selectedCategoryType)}
                        >
                            {this.renderCell(column.name, rowItem, selectedCategoryType)}
                        </TableCell>

                    );
                })}
            </TableRow>
        );
    };
    /**
 * Render cell content based on category and column.
 * For License category:
 * - 'code' column: two-line layout (code + "Ord: {orderId}")
 * - 'license' column: type name + LicenseStatusPill
 */
    renderCell = (columnName: string, rowItem: any, selectedCategoryType: CategoryType): React.ReactNode => {
        if (selectedCategoryType !== CategoryType.License) {
            // Default rendering for non-License categories
            return (
                <div
                    style={{
                        height: 20,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {rowItem[columnName]}
                </div>
            );
        }

        const licenseData = rowItem as LicenseData;

        // Custom rendering for License category
        if (columnName === 'code') {
            // Two-line activation code cell: code + "Ord: {orderId}"
            const orderId = licenseData.orderid || '�';
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div
                        style={{
                            height: 20,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {(licenseData.activationcode && licenseData.activationcode.code) || ''}
                    </div>
                    <div
                        style={{
                            height: 16,
                            fontSize: '11px',
                            color: '#666',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                        }}
                        title={`Order ID: ${orderId}`}
                    >
                        Ord: {orderId}
                    </div>
                </div>
            );
        }

        if (columnName === 'license') {
            // License cell: type name + LicenseStatusPill
            const licenseName = (licenseData.licensetype && licenseData.licensetype.typename) || '';
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', maxWidth: '100%', overflow: 'hidden' }}>
                    <span
                        style={{
                            flex: '1 1 auto',
                            minWidth: '80px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={licenseName}
                    >
                        {licenseName}
                    </span>
                    <LicenseStatusPill
                        status={licenseData.licensestatus || 'Unused'}
                        activationCount={licenseData.activationcount || 0}
                    />
                </div>
            );
        }

        // Default rendering for other License columns
        return (
            <div
                style={{
                    height: 20,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                }}
            >
                {rowItem[columnName]}
            </div>
        );
    };

    /**
     * Get title (tooltip) for a cell.
     */
    getCellTitle = (columnName: string, rowItem: any, selectedCategoryType: CategoryType): string => {
        if (selectedCategoryType === CategoryType.License && columnName === 'code') {
            const licenseData = rowItem as LicenseData;
            const orderId = licenseData.orderid || '�';
            return `${(licenseData.activationcode && licenseData.activationcode.code) || ''} | Order ID: ${orderId}`;
        }
        return rowItem[columnName] || '';
    };

    gridGraphqlExtras(): GridGraphqlExtras {
        const { sortOrder, sortOrderBy, pageSize, currentPage, filters } = this.state;
        const extras: GridGraphqlExtras = {
            pageno: Number(currentPage) + 1,
            pagesize: Number(pageSize)
        };
        if (sortOrderBy) {
            extras.orderby = `${sortOrderBy}:${sortOrder}`;
        }
        if (filters && filters.length >= 1) {
            extras.filters = filters;
        }
        return extras;
    }

    queryKey() {
        return JSON.stringify(this.gridGraphqlExtras());
    }
    getBody() {
        let selectedColumnNames: any = '';
        let requestBodyForGrid: string = '{ ';

        let allservicecaseQuery = '%s';
        let summaryQuery = 'summary { %s }';
        let instrumentQuery = 'instrument { %s }';
        let assignedQuery = 'assigneduser { %s }';
        let closedbyUserQuery = 'closedbyuser { %s }';

        let allservicecaseQueryIncluded = false;
        let isInstrumentQueryIncluded = false;
        let isProductmodelIncluded = false;
        let isAssignedQueryIncluded = false;
        let issummaryQueryQueryIncluded = false;
        let isclosedbyUserQuery = false;

        const cols = this.props.selectedCategory !== undefined ? this.props.selectedCategory.columns.filter(function (c: any) { return c.display === true; }) : [];
        if (cols.length > 0) {
            selectedColumnNames = cols.map(function (n: any) {
                return n.name;
            });
            for (const column in selectedColumnNames) {
                const colName = selectedColumnNames[column];
                switch (colName) {
                    case 'partnumber':
                    case 'description':
                        instrumentQuery = !isInstrumentQueryIncluded ? instrumentQuery.replace('%s', colName + '%s') : instrumentQuery.replace('%s', `, ${colName}%s`);
                        isInstrumentQueryIncluded = true;
                        break;
                    case 'productline':
                    case 'model':
                        if (!isProductmodelIncluded) {
                            instrumentQuery = !isInstrumentQueryIncluded ?
                             instrumentQuery.replace('%s', 'productmodel { product { displayname, twodproductname{ name } } , model{ modelname, twodproductmodel{ name } }}%s') :
                              instrumentQuery.replace('%s', ', productmodel { product { displayname, twodproductname{ name } } , model{ modelname, twodproductmodel{ name } }}%s');
                        }
                        isInstrumentQueryIncluded = true;
                        isProductmodelIncluded = true;
                        break;
                    case 'username':
                        allservicecaseQuery = !allservicecaseQueryIncluded ? allservicecaseQuery.replace('%s', 'status%s') : allservicecaseQuery.replace('%s', ', status%s');
                        assignedQuery = !isAssignedQueryIncluded ? assignedQuery.replace('%s', 'firstname, lastname%s') : assignedQuery.replace('%s', ', firstname, lastname%s');
                        closedbyUserQuery = closedbyUserQuery.replace('%s', 'firstname, lastname');
                        allservicecaseQueryIncluded = true;
                        isAssignedQueryIncluded = true;
                        isclosedbyUserQuery = true;
                        break;
                    case 'company':
                        assignedQuery = !isAssignedQueryIncluded ?
                            assignedQuery.replace('%s', 'company{ companyname }%s') : assignedQuery.replace('%s', ', company{ companyname }%s');
                        isAssignedQueryIncluded = true;
                        break;
                    case 'repairdescription':
                    case 'repairtime':
                    case 'symptom':
                    case 'failure':
                    case 'stepstakentoverifyfailure':
                    case 'productanalysis':
                    case 'warrantylabour':
                    case 'billablelabour':
                    case "labourtype":
                    case 'swonumber':
                        summaryQuery = !issummaryQueryQueryIncluded ? summaryQuery.replace('%s', colName + '%s') : summaryQuery.replace('%s', `, ${colName}%s`);
                        issummaryQueryQueryIncluded = true;
                        break;
                    default:
                        allservicecaseQuery = !allservicecaseQueryIncluded ? allservicecaseQuery.replace('%s', colName + '%s') : allservicecaseQuery.replace('%s', `, ${colName}%s`);
                        allservicecaseQueryIncluded = true;
                        break;
                }
            }
            if (allservicecaseQueryIncluded) {
                requestBodyForGrid = requestBodyForGrid + allservicecaseQuery.replace('%s', '');
            }
            if (issummaryQueryQueryIncluded) {
                requestBodyForGrid = `${requestBodyForGrid}, ${summaryQuery.replace('%s', '')}`;
            }
            if (isInstrumentQueryIncluded) {
                requestBodyForGrid = `${requestBodyForGrid}, ${instrumentQuery.replace('%s', '')}`;;
            }
            if (isAssignedQueryIncluded) {
                requestBodyForGrid = `${requestBodyForGrid}, ${assignedQuery.replace('%s', '')}`;
            }
            if (isclosedbyUserQuery) {
                requestBodyForGrid = `${requestBodyForGrid}, ${closedbyUserQuery.replace('%s', '')}`;
            }
            requestBodyForGrid = requestBodyForGrid + '}';
        }
        return requestBodyForGrid;
    }
    loadData(isToggleViewColumn = false) {
        const extras = this.gridGraphqlExtras();
        const queryKey = this.queryKey();
        if (queryKey === this.lastQuery && !isToggleViewColumn) {
            return;
        }

        const requestBodyForGrid = this.getBody();

        this.props.requestGridData(
            this.props.selectedCategory !== undefined ? this.props.selectedCategory.id : 0,
            this.props.startDate,
            this.props.endDate,
            extras,
            requestBodyForGrid
        );
        this.lastQuery = queryKey;
    }

    changeSorting = (sortColumnName: string) => {
        if (navigator.onLine) {
            const sortOrderBy = sortColumnName;
            let sortOrder: 'desc' | 'asc' = 'desc';

            if (
                this.state.sortOrderBy === sortOrderBy &&
                this.state.sortOrder === 'desc'
            ) {
                sortOrder = 'asc';
            }
            this.setState((prevState: any, props: any) => {
                if (prevState.sortOrderBy !== sortOrderBy || prevState.sortOrder !== sortOrder) {
                    return {
                        sortOrderBy,
                        sortOrder,
                        currentPage: 0
                    };
                }
                else {
                    return {
                        sortOrderBy,
                        sortOrder
                    };
                }
            }, () => {
                this.loadData();
            });
        }
    };
    changeCurrentPage = (event: any, currentPage: number) => {
        if (navigator.onLine) {
            this.setState({ currentPage }, () => {
                this.loadData();
            });
        }
    };
    changeRowsPerPage = (event: any) => {
        if (navigator.onLine) {
            this.setState({ pageSize: event.target.value }, () => {
                this.loadData();
            });
        }
    };
    onSearch = (filters: any) => {
        const formattedFilters = filters.suggestionsFilter.map((filter: any) => {
            let jsondate = '';
            if (
                filter.filterProp === 'opendate' ||
                filter.filterProp === 'closeddate'
            ) {
                jsondate = new Date(filter.filterText).toJSON();
                return { ...filter, filterText: `"${jsondate}"` };
            }
            if (filter.filterProp === 'id' || filter.filterProp === 'age') {
                return { ...filter, filterText: parseInt(filter.filterText) };
            }
            let x: any = filter.filterText;
            if (x.indexOf('\\') !== -1) {
                x = x.replace(/\\/g, '\\\\');
            }
            if (x.indexOf('"') !== -1) {
                x = x.replace(/"/g, '\\"');
            }

            return { ...filter, filterText: `"${x}"` };
        });
        this.setState((prevState: any, props: any) => {
            if (prevState.filters !== formattedFilters) {
                return {
                    filters: formattedFilters,
                    currentPage: 0
                };
            }
            else {
                return {
                    filters: formattedFilters
                };
            }
        }, () => {
            this.loadData();
            this.props.refreshChart !== undefined && this.props.refreshChart(this.state.filters);
        });
    };

    changeSelection(selectionData: any) {
        this.setState({ selection: selectionData });
    }
    exportList: Array<object> = [{ id: 0, name: 'CSV' }, { id: 1, name: 'PDF' }];
    handleExport = async (exportType: any, exportColumns: any) => {
        const token: string = loadFromLocalStorage('AuthToken');
        const encodedToken = 'Bearer ' + token;
        const headerDict: any = {
            Authorization: encodedToken,
            'Content-Type': 'application/json'
        };
        const selectedCategory: Category = this.props.selectedCategory === undefined ? categories[0] : this.props.selectedCategory;
        let mode: 'p' | 'l' = 'p';
        let url = '', columnHeaders: Object = {}, fileName = 'default';
        if (selectedCategory.type === CategoryType.ServiceCase) {
            if (selectedCategory.id === 5 && this.props.startDate !== undefined && this.props.endDate !== undefined) {
                columnHeaders = exportAllServiceCaseColumnAttributesPdf;
                mode = 'l';
            }
            else if (this.props.startDate !== undefined && this.props.endDate !== undefined) {
                columnHeaders = exportServiceCaseColumnAttributesPdf;
            }

            url = fetchServiceCasesEndPoint;
            fileName = 'ServiceCaseData';
        }
        else if (selectedCategory.type === CategoryType.License) {
            url = fetchLicensesEndPoint;
            columnHeaders = exportLicenseColumnAttributesPdf;
            fileName = 'LicenseData';
        }

        let request;
        if (selectedCategory.id === 0 || selectedCategory.id === 1 || selectedCategory.id === 2 || selectedCategory.id === 3 || selectedCategory.id === 5) {
            request = selectedCategory.requestBodyForExport.replace('%r', this.getBody());
        }
        else {
            request = selectedCategory.requestBodyForGrid;
        }
        try {
            if (navigator.onLine) {
                const response = await fetch(url, {
                    headers: new Headers(headerDict),
                    body: toServiceCaseGridGraphqlBody(
                        request,
                        selectedCategory.id,
                        this.props.startDate,
                        this.props.endDate),
                    method: 'POST'
                });
                const responseData = await response.json();
                let data: any;
                switch (selectedCategory.id) {
                    case 0:
                        data = responseData.data.myopenservicecases.data;
                        break;
                    case 1:
                        data = responseData.data.myclosedservicecases.data;
                        break;
                    case 2:
                        data = responseData.data.mycompanyopenservicecases.data;
                        break;
                    case 3:
                        data = responseData.data.mycompanyclosedservicecases.data;
                        break;
                    case 4:
                        data = responseData.data.mylicenses.data;
                        break;
                    case 5:
                        data = responseData.data.allservicecases.data;
                        break;
                }
                const rowData = getGridData(data, selectedCategory, false);

                const columns: any = [];
                const rows: any[] = [];
                exportColumns.map((column: any) => {
                    if (column.display === true) {
                        columns.push({ title: column.title, dataKey: column.title });
                    }
                });
                rowData !== undefined && rowData.map((row: any) => {
                    const temp: any = {};
                    exportColumns.map((column: any) => {
                        if (column.display === true) {
                            temp[column.title] = row[column.name];
                        }
                    });
                    rows.push(temp);
                });
                switch (exportType) {
                    case 0:
                        this.exportCsv(rows, columns.map((value: any) => { return value.title; }), fileName);
                        break;
                    case 1:
                        this.exportPdf(rows, columns, columnHeaders, fileName, mode);
                        break;
                }
            }
        } catch (e) { }
    };

    getTotalCountOnExport = async () => {

        if (this.props.selectedCategory !== undefined) {
            const selectedCategory: Category = this.props.selectedCategory;
            let url = '';
            let exportTotalCount = 0;

            if (selectedCategory.type === CategoryType.ServiceCase) {
                url = fetchServiceCasesEndPoint;
            }
            else if (selectedCategory.type === CategoryType.License) {
                url = fetchLicensesEndPoint;
            }
            if (selectedCategory.requestBodyForGridTotalCount !== undefined) {
                const body = toServiceCaseGridGraphqlBody(
                    selectedCategory.requestBodyForGridTotalCount,
                    selectedCategory.id,
                    this.props.startDate,
                    this.props.endDate,
                    { pageno: 1, pagesize: 10 });
                const responseData = await sendRequestAndReceiveResponse(body, url);
                switch (selectedCategory.id) {
                    case 0:
                        exportTotalCount = responseData.data.myopenservicecases.count;
                        break;
                    case 1:
                        exportTotalCount = responseData.data.myclosedservicecases.count;
                        break;
                    case 2:
                        exportTotalCount = responseData.data.mycompanyopenservicecases.count;
                        break;
                    case 3:
                        exportTotalCount = responseData.data.mycompanyclosedservicecases.count;
                        break;
                    case 4:
                        exportTotalCount = responseData.data.mylicenses.count;
                        break;
                    case 5:
                        exportTotalCount = responseData.data.allservicecases.count;
                        break;
                }
            }
            return exportTotalCount;
        }
    };

    exportCsv(rows: any[], columns: any[], fileName: string) {
        const escapeCell = (cell: any) => {
            const s = cell == null ? '' : String(cell);
            const needsQuotes = /[",\n]/.test(s);
            const escaped = s.replace(/"/g, '""');
            return needsQuotes ? `"${escaped}"` : escaped;
        };
        const header = columns.map((c: any) => escapeCell(c)).join(',');
        const body = rows.map(row => columns.map((col: any) => escapeCell(row[col])).join(',')).join('\n');
        const csv = `${header}\n${body}`;
        const hiddenElement = document.createElement('a');
        const csvData = new Blob([csv], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvData);
        hiddenElement.href = csvUrl;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${fileName}.csv`;
        hiddenElement.click();
    }
    exportPdf(rows: any, columns: any, columnHeaders: Object, fileName: string, mode: string) {

        /*
    
         An Open issue in the JSPDF - AUTOTABLE
    
         https://github.com/simonbengtsson/jsPDF-AutoTable/issues/469
    
         Row Content is NOT properly wrapped to corresponding cells in last rows (on few pages) and cutoff on next page
    
        */

        const doc = new jsPDF(mode as any, 'pt', 'a3');
        autoTable(doc as any, {
            columns,
            body: rows,
            //  html: "#gridTable"
            theme: 'grid',
            styles: {
                halign: 'center',
                overflow: 'linebreak',

            },
            headStyles: {
                fillColor: [33, 150, 243],
                halign: 'center'
            },
            margin: {
                top: 100
            },
            columnStyles: columnHeaders as any
        });

        doc.save(`${fileName}.pdf`);
    }

    handleSelectAllClick = (event: any) => {
        if (event.target.checked) {
            this.setState((state: any) => ({
                selected: this.props.gridData.map((rowItem: any) => rowItem.id)
            }));
            return;
        }
        this.setState({ selected: [] });
    };

    handleClick = (event: any, id: number, rowItem: any) => {
        let { selected } = this.state;
        const selectedIndex = selected.indexOf(id);
        if (this.state.isSelectOnlyOne && selectedIndex !== 0) {
            selected = [];
        }
        let newSelected: any = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        this.setState({ selected: newSelected }, () => {
            if (this.state.selected.length === 0) {
                this.setState({ selectedObject: [] });
            }
            else {
                this.setState({ selectedObject: rowItem });
            }
        });

    };
    isSelected = (id: number) => this.state.selected.indexOf(id) !== -1;

    renderAgingLegends(classes: any) {
        return (<LegacyGrid item md={8} style={{ paddingTop: '5px' }}>
            <em className={classes.agingText}>Aging Text Legend </em>
            <span>
                <Avatar
                    className={classNames(classes.avatar, classes.blackAvatar)}
                />
                {` < 7 days`}
                <Avatar
                    className={classNames(classes.avatar, classes.orangeAvatar)}
                />
                {` 7- 30 days`}
                <Avatar
                    className={classNames(classes.avatar, classes.redAvatar)}
                />
                {` > 30 days`}
            </span>
        </LegacyGrid>);
    }

    renderExpireLegends(classes: any) {
        return (<LegacyGrid item md={8} style={{ paddingTop: '5px' }}>
            <span>
                <Avatar
                    className={classNames(classes.avatar, classes.orangeAvatar)}
                />
                <em className={classes.agingText}>{`: Expires in < 7 days `}</em>
            </span>
            <span>
                <Avatar
                    className={classNames(classes.avatar, classes.greyAvatar)}
                />
            </span>
            <em className={classes.agingText}> :  Expired </em>

            <span style={{ marginLeft: '20px' }}>
                <span className={classNames(classes.legendPill, classes.greenAvatar)} />
                <em className={classes.agingText}>{` Activated `}</em>
            </span>
            <span style={{ marginLeft: '12px' }}>
                <span className={classes.legendPill} style={{ backgroundColor: '#9E9E9E' }} />
                <em className={classes.agingText}>{` Unused `}</em>
            </span>
            <span style={{ marginLeft: '12px' }}>
                <span className={classNames(classes.legendPill, classes.blueAvatar)} />
                <em className={classes.agingText}>{` Auto-provisioned `}</em>
            </span>
            <span style={{ marginLeft: '14px' }}>
                <em className={classes.agingText}>{` Number = activations done `}</em>
            </span>
        </LegacyGrid>);
    }
    toggleViewColumn = (index: number) => {

        this.setState(
            (prevState: any, props: any) => {
                const columns = Object.assign([], prevState.columns);
                columns[index].display = columns[index].display !== true;
                return {
                    columns
                };
            }, () => { this.loadData(true); });
    };
    public render(): React.ReactNode {
        const {
            pageSizes,
            sortOrder,
            sortOrderBy,
            pageSize,
            currentPage,
            selected,
            columns
        } = this.state;
        const { classes, gridData, totalCount, error } = this.props;
        const selectedColumns: any = this.state.columns.filter((x: any) => {
            return x.display === true;
        });
        const loading = this.props.isLoading;
        const filteredSearchSuggestions = this.props.selectedCategory !== undefined && this.props.selectedCategory.filters().filter(
            (suggestion: any) => {
                return (
                    this.state.filters.filter((x: any) => {
                        return x.filterProp === suggestion.searchProp;
                    }).length === 0
                );
            }
        );
        const numSelected = selected.length;
        //this.setState({ rows: rows })
        return (
            <LegacyGrid>
                <LegacyGrid container={true}>
                    {(this.props.selectedCategory !== undefined ? this.props.selectedCategory.type === CategoryType.ServiceCase ?
                        this.renderAgingLegends(classes) :
                        this.props.selectedCategory.type === CategoryType.License &&
                        this.renderExpireLegends(classes)
                        : <div />
                    )}
                    <LegacyGrid
                        item
                        style={{
                            display: 'flex',
                            alignItems: 'right',
                            flex: '1',
                            justifyContent: 'flex-end'
                        }}
                    >
                        {(this.props.selectedCategory !== undefined && this.props.selectedCategory.isAssignToEnabled === true) && (
                            <SimpleDialogDemo
                                assigntodisabled={
                                    this.state.selected.length === 0
                                }
                                serviceCaseList={this.state.selected}
                            />
                        )}
                        {this.props.selectedCategory !== undefined && this.props.selectedCategory.isOpenEnabled === true && <Open selected={this.state.selected} />}
                        {this.props.selectedCategory !== undefined && this.props.selectedCategory.isEditEnabled === true &&
                            <Edit selected={this.state.selectedObject} selectedCategory={this.props.selectedCategory} />}
                        {(this.props.selectedCategory !== undefined && this.props.selectedCategory.isExportEnabled === true) &&
                            <Export selectedColumns={selectedColumns} isDisabled={false} exportList={this.exportList} handleExport={this.handleExport} totalCount={totalCount}
                                getTotalCountOnExport={this.getTotalCountOnExport} isSearchApplied={this.state.filters.length > 0} selectTitle={`Export`} />
                        }
                    </LegacyGrid>
                </LegacyGrid>
                <br />
                <LegacyGrid>
                    <Paper>
                        <LegacyGrid container={true}>
                            <LegacyGrid item md={10}>
                                <SearchSection
                                    onSearch={this.onSearch}
                                    searchSuggestions={filteredSearchSuggestions}
                                />
                            </LegacyGrid>
                            <LegacyGrid item md={2} style={{ display: 'flex' }}>
                                <div className={classes.root}>
                                    <Button id="showColumnButton" onClick={this.resetColumnWidths}
                                            className={classes.showColumnButton}>
                                        <RestartAltIcon />
                                    </Button>
                                </div>
                                {this.props.selectedCategory !== undefined &&
                                    <TableViewCol columns={columns} onColumnUpdate={this.toggleViewColumn} />
                                }
                            </LegacyGrid>
                        </LegacyGrid>
                        <TableContainer component={Paper} className="containertable">
                            <Table id="gridTable" className="resizabletable">
                                <TableHead>
                                    <TableRow className="dtable-row-head">
                                        {(this.props.selectedCategory !== undefined && this.state.isCheckBoxEnabled === true && gridData != null && gridData.length !== 0) && (
                                        <TableCell className="dtable-cell tableCell" padding="checkbox" style={{
                                            minWidth:70,
                                        }}>
                                                <Checkbox
                                                    indeterminate={
                                                        numSelected > 0 &&
                                                        numSelected < (gridData && gridData.length)
                                                    }
                                                    checked={
                                                        numSelected === (gridData && gridData.length)
                                                    }
                                                    onChange={this.handleSelectAllClick}
                                                    hidden={this.state.isSelectOnlyOne}
                                                />
                                            </TableCell>
                                        )}
                                        {this.props.selectedCategory !== undefined && columns.map((column: any,index:any) => {
                                            return (
                                                (column.display === true) && (<TableCell
                                                    className="dtable-cell tableCell"
                                                    key={column.name}
                                                    sortDirection={
                                                        (sortOrderBy === column.name
                                                            ? sortOrder
                                                            : false) as 'asc' | 'desc' | false
                                                    }
                                                    style={{
                                                        width: 'auto',
                                                        fontWeight: 'bolder'
                                                    }}
                                                >
                                                    <TableSort
                                                        active={sortOrderBy === column.name}
                                                        direction={sortOrder as 'asc' | 'desc'}
                                                        onClick={this.changeSorting}
                                                        sortByColumn={column.name}
                                                    >
                                                        {column.title}
                                                    </TableSort>
                                                    <div
                                                        onMouseDown={() => this.onClickResizeColumn(index)}
                                                        ref={this.columnRefs[index]}
                                                        className={"resizeLine"}
                                                    />
                                                </TableCell>)

                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        (gridData != null &&
                                            gridData.length !== 0) && error === undefined ?
                                            gridData.map((rowItem: any) => {
                                                const isSelected = this.isSelected(rowItem.id);
                                                return this.getTableRow(rowItem, isSelected, this.props.selectedCategory !== undefined ?
                                                    this.props.selectedCategory.type : CategoryType.ServiceCase);
                                            }) :
                                            !this.props.isLoading && error !== undefined ?
                                                <TableRow>
                                                    <TableCell id="norecords" colSpan={12} align='center' className='no-records'>
                                                        {error.code === 401 && error.message ? error.message : 'An error occurred while processing this request.'}
                                                    </TableCell>
                                                </TableRow> :
                                                !this.props.isLoading &&
                                                <TableRow>
                                                    <TableCell id="norecords" colSpan={12} align='center' className='no-records'>No records to display</TableCell>
                                                </TableRow>
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {loading && <LinearProgress color="secondary" />}
                                                        <TablePagination
                                                            rowsPerPageOptions={pageSizes}
                                                            component="div"
                                                            count={totalCount === undefined ? 0 : totalCount}
                                                            rowsPerPage={pageSize}
                                                            page={currentPage}
                                                            backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                                            nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                                            onPageChange={(_e, newPage) => this.changeCurrentPage(_e, newPage)}
                                                            onRowsPerPageChange={this.changeRowsPerPage}
                                                        />
                    </Paper>
                </LegacyGrid>
            </LegacyGrid>
        );
    }
}
function getGridData(
    data: any,
    selectedCategory: Category,
    isLoading: boolean
) {
    if (
        data === undefined ||
        data.length === 0 ||
        data.length === undefined ||
        isLoading === true ||
        selectedCategory === undefined
    ) {
        return undefined;
    }
    const rows: Array<Object> = [];
    switch (selectedCategory.type) {
        case CategoryType.ServiceCase:
            data.map((x: ServiceCaseData) => {
                const summary = x.summary;
                let repairdescription, symptom, failure, repairtime, swonumber = '-', stepstakentoverifyfailure, productanalysis, warrantylabour, billablelabour, labourtype;
                if (summary != null && Array.isArray(summary) && summary.length) {
                    const summaryValue = summary[0];
                    if (summaryValue.repairdescription !== null) {
                        repairdescription = summaryValue.repairdescription;
                    }
                    if (summaryValue.symptom !== null) {
                        symptom = summaryValue.symptom;
                    }
                    if (summaryValue.failure !== null) {
                        failure = summaryValue.failure;
                    }
                    if (summaryValue.repairtime !== null) {
                        repairtime = summaryValue.repairtime;
                    }
                    if (summaryValue.stepstakentoverifyfailure !== null) {
                        stepstakentoverifyfailure = summaryValue.stepstakentoverifyfailure;
                    }
                    if (summaryValue.productanalysis !== null) {
                        productanalysis = summaryValue.productanalysis;
                    }
                    if (summaryValue.warrantylabour !== null) {
                        warrantylabour = summaryValue.warrantylabour;
                    }
                    if (summaryValue.billablelabour !== null) {
                        billablelabour = summaryValue.billablelabour;
                    }
                    if (summaryValue.swonumber != null) {
                        swonumber = summaryValue.swonumber;
                    }
                    if (summaryValue.labourtype !== null) {
                        labourtype = summaryValue.labourtype;
                    }
                }

                rows.push({
                    swonumber,
                    gsbusinessarea: x.gsbusinessarea,
                    stepstakentoverifyfailure,
                    productanalysis,
                    warrantylabour,
                    billablelabour,
                    labourtype,
                    servicebulletin: x.servicebulletin,
                    id: x.id,
                    serialnumber: x.serialnumber,
                    partnumber: (x.instrument !== undefined && x.instrument != null) ? x.instrument.partnumber : '',
                    description: (x.instrument !== undefined && x.instrument != null) ? x.instrument.description : '',
                    username:
                        x.status === 1 ? (x.assigneduser !== undefined) ?
                            `${x.assigneduser.firstname} ${x.assigneduser.lastname}` : '' : (x.closedbyuser !== undefined && x.closedbyuser != null) ?
                            `${x.closedbyuser.firstname} ${x.closedbyuser.lastname}` : '-',
                    company: x.assigneduser !== undefined && x.assigneduser.company !== undefined && x.assigneduser.company != null ? x.assigneduser.company.companyname : '-',
                    opendate: new Date(x.opendate + 'Z').toLocaleDateString(),
                    updateddate: new Date(x.updateddate + 'Z').toLocaleDateString(),
                    closeddate:
                        x.closeddate == null
                            ? '-'
                            : new Date(x.closeddate + 'Z').toLocaleDateString(),

                    productline: (x.instrument !== undefined && x.instrument.productmodel !== undefined && x.instrument.productmodel.product !== undefined &&
                        x.instrument.productmodel.product != null && x.instrument.productmodel.product.twodproductname != null) ?
                        x.instrument.productmodel.product.twodproductname.name : ' ',
                    model: (x.instrument !== undefined && x.instrument.productmodel !== undefined && x.instrument.productmodel.model !== undefined &&
                        x.instrument.productmodel.model != null && x.instrument.productmodel.model.twodproductmodel != null) ?
                        x.instrument.productmodel.model.twodproductmodel.name : ' ',
                    warrantystatus: x.warrantystatus,
                    failuredescription: x.failuredescription,
                    repairdescription,
                    symptom,
                    failure,
                    servicepart: x.servicepart,
                    servicepartserialnumber: x.servicepartserialnumber,
                    serviceoperation: x.serviceoperation,
                    repairtime,
                    age: x.age
                });
            });
            break;
        case CategoryType.License:
            data.map((x: LicenseData) => {
                const activationCode = x.activationcode != null ? x.activationcode.code : '';
                const licenseTypeName = x.licensetype != null ? x.licensetype.typename : '';
                const subscription = x.subscription != null ? x.subscription : [];

                rows.push({
                    id: x.licenseid,
                    username: x.user != null ? `${x.user.firstname} ${x.user.lastname}` : '',
                    emailid: x.user != null ? x.user.emailid : '',
                    company: (x.user != null && x.user.company != null) ? x.user.company.companyname : x.company != null ? x.company.companyname : '',
                    machinename: x.machinename,
                    // Flat fields for sorting/export/default rendering
                    code: activationCode,
                    license: licenseTypeName,
                    // Nested + computed fields required by renderCell (License category)
                    activationcode: x.activationcode,
                    licensetype: x.licensetype,
                    orderid: x.orderid,
                    licensestatus: x.licensestatus,
                    activationcount: x.activationcount,
                    enddate: subscription.length !== 0 ? new Date(subscription[0].enddate + 'Z').toLocaleDateString() : '',
                    expiresin: getDateDiff(new Date(), subscription.length !== 0 ? new Date(subscription[0].enddate + 'Z') : new Date())                });
            });
            break;
        case CategoryType.User:
            data.map((x: User) => {
                const rolestring = '';
                const roleid = '';
                rows.push({
                    id: x.userid,
                    username: `${x.firstname} ${x.lastname}`,
                    status: x.status != null ? x.status === true ? 'Active' : 'InActive' : '',
                    email: x.emailid,
                    company: x.company != null ? x.company.companyname : '',
                    usertype: x.internaluser === 'Y' ? 'Internal' : 'External',
                    roles: x.userrolemapping.reduce((a: any, userrolemapping: UserRoleMapping) => {
                        return a + ` | ${userrolemapping.userrole.rolename}`;
                    }, rolestring).substr(2),
                    roleids: x.userrolemapping.reduce((a: any, userrolemapping: UserRoleMapping) => {
                        return a + `,${userrolemapping.userrole.roleid}`;
                    }, roleid).substr(1),
                    serviceLevel: (x.userservicelevelmapping !== undefined && x.userservicelevelmapping.length > 0) ?
                        x.userservicelevelmapping[0].serviceLevel.serviceLevelValue : (x.internaluser === 'Y') ? 4 : 3
                });
            });
            break;
        case CategoryType.UserRole:
            const modules = '';
            data.map((x: UserRole) => {
                const moduleList: Array<Module> = [];
                x.rolemodulemapping.forEach((rolemodulemapping: RoleModuleMapping) => {
                    moduleList.push({ id: rolemodulemapping.module.id, modulename: rolemodulemapping.module.modulename });
                });
                rows.push({
                    id: x.roleid,
                    userrole: x.rolename,
                    modules: x.rolemodulemapping.reduce((a: any, rolemodulemapping: RoleModuleMapping) => {
                        return a + ` | ${rolemodulemapping.module.modulename}`;
                    }, modules).substr(2),
                    selectedModules: moduleList
                });
            });
            break;
    }
    return rows;
}

const mapStateToProps = (state: any) => ({
    state: state.dataTable,
    isLoading: state.dataTable.isLoading,
    gridData: getGridData(
        state.dataTable.gridData,
        state.dataTable.selectedCategory,
        state.dataTable.isLoading
    ),
    totalCount: state.dataTable.totalCount,
    error: state.dataTable.error
});

const mapDispatchtoProps = (dispatch: any) =>
    bindActionCreators(actionCreators, dispatch);

const StyledDataTable = withStyles(styles, { withTheme: true })(DataTable as any);
export default connect(
    mapStateToProps,
    mapDispatchtoProps
)(StyledDataTable) as React.ComponentType<Partial<Props>>;
